import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MaintenanceInstallationSearchSelect } from '@/products/maintenance/features/maintenance-installation-search-select';
import { MaintenanceStatusBadge } from '@/products/maintenance/features/maintenance-status-badge';
import {
  useAddMaintenanceTemplateScope,
  useCreateMaintenanceTemplateVersion,
  useMaintenanceTemplate,
  usePublishMaintenanceTemplateVersion,
  useUpdateMaintenanceTemplate,
} from '@/products/maintenance/features/use-maintenance-templates';
import { MaintenanceTemplateDeleteButton } from '@/products/maintenance/features/templates/maintenance-template-delete-button';
import { MaintenanceTemplateScopeRow } from '@/products/maintenance/features/templates/maintenance-template-scope-row';
import {
  MAINTENANCE_TEMPLATES_PATH,
  maintenanceTemplateVersionPath,
} from '@/products/maintenance/maintenance-paths';

export function MaintenanceTemplateDetailPage() {
  const copy = useVoiceCopy();
  const { templateId = '' } = useParams();
  const template = useMaintenanceTemplate(templateId);
  const createVersion = useCreateMaintenanceTemplateVersion(templateId);
  const publish = usePublishMaintenanceTemplateVersion(templateId);
  const update = useUpdateMaintenanceTemplate(templateId);
  const addScope = useAddMaintenanceTemplateScope(templateId);
  const navigate = useNavigate();
  const [scopeInstallationId, setScopeInstallationId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [profileKey, setProfileKey] = useState('');

  usePageTitle(template.data?.name ?? 'Vorlage');

  if (template.isPending) {
    return <p className="text-sm text-muted-foreground">Vorlage wird geladen …</p>;
  }
  if (template.isError || !template.data) {
    return <p className="text-sm text-destructive">Die Vorlage wurde nicht gefunden.</p>;
  }

  const row = template.data;
  const draft = row.versions.find((v) => v.status === 'draft');
  const nextProfileKey = `${row.id}:${row.updatedAt}`;
  if (profileKey !== nextProfileKey) {
    setProfileKey(nextProfileKey);
    setName(row.name);
    setDescription(row.description ?? '');
  }

  return (
    <div className="space-y-8">
      <PlatformPageHeader title={row.name} description={row.description ?? undefined} />
      <p className="text-sm">
        <Link className="text-primary hover:underline" to={MAINTENANCE_TEMPLATES_PATH}>
          ← Zurück zur Vorlagenliste
        </Link>
      </p>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={Boolean(draft) || createVersion.isPending}
          onClick={() => {
            void createVersion.mutateAsync().catch(() => {
              toast.error('Die neue Version konnte nicht angelegt werden.');
            });
          }}
        >
          Neue Entwurfsversion
        </Button>
        {draft ? (
          <Button
            type="button"
            disabled={publish.isPending}
            onClick={() => {
              void publish.mutateAsync(draft.id).catch((err: unknown) => {
                const message =
                  err instanceof ApiRequestError
                    ? err.message
                    : copy('maintenance.template.publish.failed');
                toast.error(message);
              });
            }}
          >
            Entwurf veröffentlichen
          </Button>
        ) : null}
        {row.isActive ? (
          <Button
            type="button"
            variant="outline"
            disabled={update.isPending}
            onClick={() => {
              void update.mutateAsync({ isActive: false }).catch(() => {
                toast.error('Die Vorlage konnte nicht deaktiviert werden.');
              });
            }}
          >
            Vorlage deaktivieren
          </Button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            disabled={update.isPending}
            onClick={() => {
              void update
                .mutateAsync({ isActive: true })
                .then(() => {
                  toast.success('Vorlage wieder aktiv.');
                })
                .catch(() => {
                  toast.error('Die Vorlage konnte nicht aktiviert werden.');
                });
            }}
          >
            Vorlage aktivieren
          </Button>
        )}
        <MaintenanceTemplateDeleteButton
          template={row}
          onDeleted={() => {
            void navigate(MAINTENANCE_TEMPLATES_PATH);
          }}
        />
      </div>

      <form
        className="grid max-w-xl gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void update
            .mutateAsync({
              name: name.trim(),
              description: description.trim() || null,
            })
            .then(() => {
              toast.success('Vorlage gespeichert.');
            })
            .catch((err: unknown) => {
              const message =
                err instanceof ApiRequestError
                  ? err.message
                  : 'Die Vorlage konnte nicht gespeichert werden.';
              toast.error(message);
            });
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="tpl-name">Name</Label>
          <Input
            id="tpl-name"
            className="min-h-11"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tpl-description">Beschreibung</Label>
          <textarea
            id="tpl-description"
            className="flex min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-fit" disabled={update.isPending || name.trim() === ''}>
          Name speichern
        </Button>
      </form>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Zuordnung für Aufträge</h2>
        <p className="text-sm text-muted-foreground">
          Beim Anlegen eines Auftrags erscheinen nur veröffentlichte Versionen dieser Vorlage, wenn
          sie mindestens einer Anlage (oder einem ganzen Projekt) zugeordnet sind.
        </p>
        {row.scopes.length > 0 ? (
          <ul className="space-y-2">
            {row.scopes.map((scope) => (
              <li key={scope.id}>
                <MaintenanceTemplateScopeRow scope={scope} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            Noch keine Anlage oder kein Projekt zugeordnet.
          </p>
        )}
        <div className="flex flex-col gap-3 rounded-lg border p-4 sm:max-w-md">
          <MaintenanceInstallationSearchSelect
            value={scopeInstallationId}
            onChange={setScopeInstallationId}
            excludeIds={row.scopes
              .map((scope) => scope.installationId)
              .filter((id): id is string => Boolean(id))}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={!scopeInstallationId || addScope.isPending}
            onClick={() => {
              if (!scopeInstallationId) {
                return;
              }
              void addScope
                .mutateAsync({ installationId: scopeInstallationId })
                .then(() => {
                  toast.success('Anlage zugeordnet.');
                  setScopeInstallationId('');
                })
                .catch((err: unknown) => {
                  const message =
                    err instanceof ApiRequestError
                      ? err.message
                      : 'Die Zuordnung konnte nicht gespeichert werden.';
                  toast.error(message);
                });
            }}
          >
            Anlage zuordnen
          </Button>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Versionen</h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nr.</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>PDF</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {row.versions.map((version) => (
              <TableRow key={version.id}>
                <TableCell>v{version.versionNumber}</TableCell>
                <TableCell>
                  <MaintenanceStatusBadge versionStatus={version.status} />
                </TableCell>
                <TableCell>{version.hasSourcePdf ? 'Ja' : '—'}</TableCell>
                <TableCell>
                  <Link
                    className="text-primary hover:underline"
                    to={maintenanceTemplateVersionPath(templateId, version.id)}
                  >
                    {version.status === 'draft' ? 'Bearbeiten' : 'Ansehen'}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}
